import React, { useState, useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  LayoutDashboard,
  CheckSquare,
  MessageSquare,
  FileText,
  ClipboardList,
  Boxes,
  Users,
  BarChart3,
  Bell,
  LucideIcon,
} from 'lucide-react'
import { HistoryContext, PageHistoryItem, HistoryContextType } from './HistoryContext.context'

const MAX_HISTORY_ITEMS = 50
const STORAGE_KEY = 'axioma_page_history'

// In-memory cache for node names to avoid repeated API calls
const nodeNameCache = new Map<string, string>()

// Node type configuration for consistent title generation and icons
const ENTITY_CONFIG = {
  workspace: { displayName: 'Workspace', icon: Boxes },
  project: { displayName: 'Project', icon: ClipboardList },
  task: { displayName: 'Task', icon: CheckSquare },
  message: { displayName: 'Message', icon: MessageSquare },
  document: { displayName: 'Document', icon: FileText },
  user: { displayName: 'User', icon: Users },
} as const

type nodeType = keyof typeof ENTITY_CONFIG

/**
 * Get node name by type and ID from cache or API
 */
async function getnodeName(nodeType: string, nodeId: string): Promise<string | null> {
  const cacheKey = `${nodeType}:${nodeId}`

  // Check cache first
  if (nodeNameCache.has(cacheKey)) {
    return nodeNameCache.get(cacheKey) || null
  }

  try {
    let nodeName: string | null = null

    switch (nodeType) {
      case 'workspace': {
        const { workspaceService } = await import('@/services/workspace.service')
        const workspace = await workspaceService.getById(nodeId)
        nodeName = workspace?.name || null
        break
      }
      case 'project': {
        const { projectService } = await import('@/services/project.service')
        const project = await projectService.getById(nodeId)
        nodeName = project?.name || null
        break
      }
      case 'task': {
        const { taskService } = await import('@/services/task.service')
        const task = await taskService.getById(nodeId)
        nodeName = task?.name || null
        break
      }
      case 'message': {
        const { messageService } = await import('@/services/message.service')
        const message = await messageService.getById(nodeId)
        nodeName = message?.name || null
        break
      }
      case 'document': {
        const { documentService } = await import('@/services/document.service')
        const document = await documentService.getById(nodeId)
        nodeName = document?.name || null
        break
      }
      case 'user': {
        const { userService } = await import('@/services/user.service')
        const user = await userService.getById(nodeId)
        nodeName = user?.name || null
        break
      }
    }

    // Cache the result (including null for not found)
    nodeNameCache.set(cacheKey, nodeName || '')

    return nodeName
  } catch (error) {
    console.warn(`Failed to fetch ${nodeType} name for ID ${nodeId}:`, error)
    nodeNameCache.set(cacheKey, '')
    return null
  }
}

/**
 * Generate title for node pages (e.g., /projects/123 -> "Project: My Project")
 */
async function generateNodePageTitle(
  nodeType: nodeType,
  nodeId: string,
  fallbackTitle: string
): Promise<{ title: string; icon?: LucideIcon }> {
  const config = ENTITY_CONFIG[nodeType]
  const nodeName = await getnodeName(nodeType, nodeId)

  return {
    title: nodeName ? `${nodeName}` : fallbackTitle,
    icon: config.icon,
  }
}

/**
 * Generate title for node action pages (e.g., /projects/123/edit -> "Edit Project: My Project")
 */
async function generateNodeActionTitle(
  nodeType: nodeType,
  nodeId: string,
  action: string,
  fallbackTitle: string
): Promise<{ title: string; icon?: LucideIcon }> {
  const config = ENTITY_CONFIG[nodeType]
  const nodeName = await getnodeName(nodeType, nodeId)

  return {
    title: nodeName ? `${action} ${config.displayName}: ${nodeName}` : fallbackTitle,
    icon: config.icon,
  }
}

export function HistoryProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const [history, setHistory] = useState<PageHistoryItem[]>([])

  // Generate a title and icon for the current page based on the path
  const generatePageTitle = useCallback(
    async (path: string): Promise<{ title: string; icon?: LucideIcon }> => {
      const pathSegments = path.split('/').filter(Boolean)

      // Handle common page patterns
      if (path === '/') return { title: 'Home', icon: LayoutDashboard }

      // Handle workspace pages using abstraction
      if (path.startsWith('/workspaces')) {
        if (pathSegments.length === 1) return { title: 'Workspaces', icon: Boxes }
        if (pathSegments.length === 2) {
          return await generateNodePageTitle(
            'workspace',
            pathSegments[1],
            `Workspace: ${pathSegments[1]}`
          )
        }
        if (path.endsWith('/edit')) {
          return await generateNodeActionTitle(
            'workspace',
            pathSegments[1],
            'Edit',
            `Edit Workspace: ${pathSegments[1]}`
          )
        }
        if (path.endsWith('/members')) {
          return await generateNodeActionTitle(
            'workspace',
            pathSegments[1],
            'Workspace Members:',
            `Workspace Members: ${pathSegments[1]}`
          )
        }
        if (path.endsWith('/invite')) {
          return await generateNodeActionTitle(
            'workspace',
            pathSegments[1],
            'Invite to',
            `Invite to Workspace: ${pathSegments[1]}`
          )
        }
      }
      // Handle project pages using abstraction
      if (path.startsWith('/projects')) {
        if (pathSegments.length === 1) return { title: 'Projects', icon: ClipboardList }
        if (pathSegments.length === 2) {
          return await generateNodePageTitle(
            'project',
            pathSegments[1],
            `Project: ${pathSegments[1]}`
          )
        }
        if (path.endsWith('/edit')) {
          return await generateNodeActionTitle(
            'project',
            pathSegments[1],
            'Edit',
            `Edit Project: ${pathSegments[1]}`
          )
        }
        if (path.endsWith('/members')) {
          return await generateNodeActionTitle(
            'project',
            pathSegments[1],
            'Project Members:',
            `Project Members: ${pathSegments[1]}`
          )
        }
        if (path.endsWith('/invite')) {
          return await generateNodeActionTitle(
            'project',
            pathSegments[1],
            'Invite to',
            `Invite to Project: ${pathSegments[1]}`
          )
        }
      }
      // Handle task pages using abstraction
      if (path.startsWith('/tasks')) {
        if (pathSegments.length === 1) return { title: 'Tasks', icon: CheckSquare }
        if (path.endsWith('/new')) return { title: 'Create Task', icon: CheckSquare }
        if (pathSegments.length === 2) {
          return await generateNodePageTitle('task', pathSegments[1], `Task: ${pathSegments[1]}`)
        }
        if (path.endsWith('/edit')) {
          return await generateNodeActionTitle(
            'task',
            pathSegments[1],
            'Edit',
            `Edit Task: ${pathSegments[1]}`
          )
        }
      }
      // Handle message pages using abstraction
      if (path.startsWith('/messages')) {
        if (pathSegments.length === 1) return { title: 'Messages', icon: MessageSquare }
        if (path.endsWith('/new')) return { title: 'Create Message', icon: MessageSquare }
        if (pathSegments.length === 2) {
          return await generateNodePageTitle(
            'message',
            pathSegments[1],
            `Message: ${pathSegments[1]}`
          )
        }
        if (path.endsWith('/edit')) {
          return await generateNodeActionTitle(
            'message',
            pathSegments[1],
            'Edit',
            `Edit Message: ${pathSegments[1]}`
          )
        }
      }
      // Handle document pages using abstraction
      if (path.startsWith('/documents')) {
        if (pathSegments.length === 1) return { title: 'Documents', icon: FileText }
        if (path.endsWith('/new')) return { title: 'Create Document', icon: FileText }
        if (pathSegments.length === 2) {
          return await generateNodePageTitle(
            'document',
            pathSegments[1],
            `Document: ${pathSegments[1]}`
          )
        }
        if (path.endsWith('/edit')) {
          return await generateNodeActionTitle(
            'document',
            pathSegments[1],
            'Edit',
            `Edit Document: ${pathSegments[1]}`
          )
        }
      }
      // Handle user pages using abstraction
      if (path.startsWith('/users')) {
        if (pathSegments.length === 1) return { title: 'Users', icon: Users }
        if (path.endsWith('/new')) return { title: 'Create User', icon: Users }
        if (pathSegments.length === 2) {
          return await generateNodePageTitle('user', pathSegments[1], `User: ${pathSegments[1]}`)
        }
        if (path.endsWith('/edit')) {
          return await generateNodeActionTitle(
            'user',
            pathSegments[1],
            'Edit',
            `Edit User: ${pathSegments[1]}`
          )
        }
      }
      if (path === '/reports') return { title: 'Reports', icon: BarChart3 }
      if (path === '/inbox') return { title: 'Inbox', icon: Bell }
      if (path === '/search') return { title: 'Search', icon: LayoutDashboard }
      if (path === '/onboarding') return { title: 'Onboarding', icon: LayoutDashboard }

      // Fallback to capitalized path segments
      return {
        title: pathSegments
          .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
          .join(' '),
        icon: LayoutDashboard,
      }
    },
    []
  )

  // Load history from localStorage on mount
  useEffect(() => {
    const loadHistory = async () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored) {
          const parsedHistory = JSON.parse(stored) as Array<Omit<PageHistoryItem, 'icon'>> & {
            icon?: unknown
          }

          // Regenerate icons for all items since they can't be serialized
          const historyWithIcons = await Promise.all(
            parsedHistory.map(async (item) => {
              const result = await generatePageTitle(item.path)
              return {
                ...item,
                icon: result.icon, // Regenerate icon from path
              }
            })
          )

          setHistory(historyWithIcons)
        }
      } catch (error) {
        console.warn('Failed to load page history from localStorage:', error)
        // If there's corrupted data, clear it
        try {
          localStorage.removeItem(STORAGE_KEY)
        } catch {
          // Ignore cleanup errors
        }
      }
    }

    loadHistory()
  }, [generatePageTitle])

  // Save history to localStorage whenever it changes (excluding icons which can't be serialized)
  useEffect(() => {
    try {
      // Strip icons before serializing since React components can't be JSON stringified
      const historyToStore = history.map(({ icon: _icon, ...item }) => item)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(historyToStore))
    } catch (error) {
      console.warn('Failed to save page history to localStorage:', error)
    }
  }, [history])

  // Populate node names for existing history items on load
  useEffect(() => {
    const updateExistingHistoryTitles = async () => {
      const updates: Promise<void>[] = []

      for (const item of history) {
        // Check if title contains an ID pattern (like "Workspace: 123" or "Project: abc")
        const titleHasId = /\w+:\s+[a-zA-Z0-9]+$/.test(item.title)

        if (titleHasId) {
          const updatePromise = generatePageTitle(item.path).then((result) => {
            if (result.title !== item.title) {
              setHistory((currentHistory) =>
                currentHistory.map((historyItem) =>
                  historyItem.id === item.id
                    ? { ...historyItem, title: result.title, icon: result.icon }
                    : historyItem
                )
              )
            }
          })
          updates.push(updatePromise)
        }
      }

      // Wait for all updates to complete
      await Promise.allSettled(updates)
    }

    if (history.length > 0) {
      updateExistingHistoryTitles()
    }
  }, [history, generatePageTitle])

  // Add a page to history
  const addToHistory = useCallback(
    async (path: string, title?: string) => {
      // Don't add if it's the same as the current last item
      setHistory((prevHistory) => {
        const lastItem = prevHistory[prevHistory.length - 1]
        if (lastItem && lastItem.path === path) {
          return prevHistory
        }

        // Remove any existing entry with the same path to avoid duplicates
        const filteredHistory = prevHistory.filter((item) => item.path !== path)

        // Create new item with provided title or generate one
        const newItem: PageHistoryItem = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          path,
          title: title || 'Loading...', // Temporary title, will be updated
          timestamp: Date.now(),
        }

        // Add new item and keep only the last MAX_HISTORY_ITEMS
        const newHistory = [...filteredHistory, newItem].slice(-MAX_HISTORY_ITEMS)

        // Update the title asynchronously if it wasn't provided
        if (!title) {
          generatePageTitle(path).then((result) => {
            setHistory((currentHistory) => {
              return currentHistory.map((item) =>
                item.id === newItem.id && item.title === 'Loading...'
                  ? { ...item, title: result.title, icon: result.icon }
                  : item
              )
            })
          })
        }

        return newHistory
      })
    },
    [generatePageTitle]
  )

  // Track route changes
  useEffect(() => {
    // Skip tracking for auth pages and 404
    if (
      location.pathname.startsWith('/login') ||
      location.pathname.startsWith('/signup') ||
      location.pathname.startsWith('/forgot-password') ||
      location.pathname === '*'
    ) {
      return
    }

    addToHistory(location.pathname + location.search)
  }, [location.pathname, location.search, addToHistory])

  // Clear history
  const clearHistory = useCallback(() => {
    setHistory([])
  }, [])

  // Remove specific item from history
  const removeFromHistory = useCallback((id: string) => {
    setHistory((prevHistory) => prevHistory.filter((item) => item.id !== id))
  }, [])

  const contextValue: HistoryContextType = {
    history,
    addToHistory,
    clearHistory,
    removeFromHistory,
  }

  return <HistoryContext.Provider value={contextValue}>{children}</HistoryContext.Provider>
}
